import produce from 'immer';

import {
  applyLiquidSetUpdate,
  getDefaultLiquidSet,
  LiquidSet,
  LiquidSetId,
  LiquidSetUpdate,
} from 'client/app/apps/disco-prototype/state/liquidSet';
import { reorderItems } from 'client/app/apps/disco-prototype/state/reorder';
import { State } from 'client/app/apps/disco-prototype/state/state';
import {
  applyStepUpdate,
  getDefaultStep,
  removeLiquidSet,
  Step,
  StepId,
  StepUpdate,
} from 'client/app/apps/disco-prototype/state/steps';
import {
  applyTableUpdate,
  Table,
  TableId,
  TableUpdate,
} from 'client/app/apps/disco-prototype/state/table';

export type Action =
  | {
      type: 'initialize';
      payload: {
        tables: Table[];
        liquidSets: LiquidSet[];
      };
    }
  | {
      type: 'updateResponse';
      tables: Table[];
      liquidSets: LiquidSet[];
      steps: Step[];
    }
  | {
      type: 'createTable';
      file: File;
    }
  | {
      type: 'updateTable';
      toUpdate: TableId;
      update: TableUpdate;
    }
  | {
      type: 'deleteTable';
      toDelete: TableId;
    }
  | {
      type: 'createLiquidSet';
      fromTable: TableId;
    }
  | {
      type: 'updateLiquidSet';
      toUpdate: LiquidSetId;
      update: LiquidSetUpdate;
    }
  | {
      type: 'deleteLiquidSet';
      toDelete: LiquidSetId;
    }
  | {
      type: 'createStep';
    }
  | {
      type: 'updateStep';
      toUpdate: StepId;
      update: StepUpdate;
    }
  | {
      type: 'reorderSteps';
      newOrder: StepId[];
    }
  | {
      type: 'deleteStep';
      toDelete: StepId;
    };

export const INITIAL_STATE = {
  tables: [],
  liquidSets: [],
  steps: [],
};

const reducer = produce((draft: State, action: Action): State => {
  switch (action.type) {
    case 'initialize': {
      const { tables, liquidSets } = action.payload;
      draft.tables = tables;
      draft.liquidSets = liquidSets;
      break;
    }
    case 'updateResponse': {
      draft.tables = action.tables;
      draft.liquidSets = action.liquidSets;
      draft.steps = action.steps;
      break;
    }
    case 'createTable': {
      // TODO: Reducer should be synchronous
      // We are removing this functionality for now
      // We will review the need for this action and figure out the way how to do this synchronously shortly if needed
      break;
    }
    case 'updateTable': {
      const table = draft.tables.find(t => t.id === action.toUpdate);
      if (table) {
        applyTableUpdate(table, action.update);
      }
      break;
    }
    case 'deleteTable': {
      draft.tables = draft.tables.filter(t => t.id !== action.toDelete);
      draft.liquidSets = draft.liquidSets.filter(ls => ls.table.id !== action.toDelete);
      removeLiquidSet(draft.steps, action.toDelete);
      break;
    }
    case 'createLiquidSet': {
      const table = draft.tables.find(t => t.id === action.fromTable);
      if (table) {
        draft.liquidSets.push(getDefaultLiquidSet(table));
      }
      break;
    }
    case 'updateLiquidSet': {
      const liquidSet = draft.liquidSets.find(ls => ls.id === action.toUpdate);
      if (liquidSet) {
        applyLiquidSetUpdate(liquidSet, action.update);
      }
      break;
    }
    case 'deleteLiquidSet': {
      draft.liquidSets = draft.liquidSets.filter(ls => ls.id !== action.toDelete);
      removeLiquidSet(draft.steps, action.toDelete);
      break;
    }
    case 'createStep': {
      const newStep = getDefaultStep(draft.steps);
      draft.steps.push(newStep);
      break;
    }
    case 'updateStep': {
      const toUpdate = draft.steps.find(s => s.id === action.toUpdate);
      toUpdate && applyStepUpdate(toUpdate, draft.liquidSets, action.update);
      break;
    }
    case 'reorderSteps': {
      draft.steps = reorderItems(draft.steps, action.newOrder);
      break;
    }
    case 'deleteStep': {
      draft.steps = draft.steps.filter(s => s.id !== action.toDelete);
      break;
    }
    default: {
      // should handle all cases
      const _exhaustive: never = action;
      console.error(`unexpected action ${_exhaustive}`);
    }
  }

  return draft;
});

export default reducer;
